/**
 * Formats French text to adhere to standard French sentence casing rules:
 * - Only the first letter of a sentence or phrase is capitalized.
 * - Subsequent words in Title Case are converted to lowercase.
 * - All-caps acronyms (e.g. SMART, OMS, WHO, FLTT, RPE, CB) are preserved.
 * - Hyphenated and slashed phrases are properly handled (e.g. "Anti-stress", "Plaisir / amusement", "Auto-évaluation").
 *
 * If the language is not French ('fr' or starting with 'fr-'), the text is returned as-is.
 *
 * @param {string} text - The input string to format.
 * @param {string} lang - The current language code (e.g., 'fr', 'en', 'es').
 * @returns {string} The formatted text.
 */
export function formatFrenchSentenceCase(text, lang = 'fr') {
  if (text == null || typeof text !== 'string') return text;
  const normalizedLang = String(lang || '').toLowerCase().trim();
  if (normalizedLang !== 'fr' && !normalizedLang.startsWith('fr-')) {
    return text;
  }

  const trimmed = text.trim();
  if (!trimmed) return trimmed;

  // Capitalize the first character
  const firstChar = trimmed.charAt(0).toUpperCase();
  const rest = trimmed.slice(1);

  // Match words that start with an uppercase letter followed by one or more lowercase letters,
  // following any delimiter (space, hyphen, slash, etc.)
  // Using unicode flag \p{Lu} and \p{Ll} to properly match accented letters (e.g. É, À, È, etc.)
  // Acronyms (e.g. SMART, OMS) do not have \p{Ll} letters, so they are preserved.
  const formattedRest = rest.replace(
    /(^|[^\p{L}\p{N}])(\p{Lu})(\p{Ll}+)/gu,
    (match, prefix, firstLetter, lowerLetters) => {
      return prefix + firstLetter.toLowerCase() + lowerLetters;
    }
  );

  return firstChar + formattedRest;
}

export default formatFrenchSentenceCase;
